import { useCallback, useState } from "react";
import { CloseTemplateWithStats } from "@common/types/closeTemplate";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useDeleteCloseTemplateMutation,
    useArchiveCloseTemplateMutation,
    useUnarchiveCloseTemplateMutation,
} from "@api/closeTemplate.api";

interface UseCloseTemplateLifecycleParams {
    templatesList: CloseTemplateWithStats[];
}

export function useCloseTemplateLifecycle({
    templatesList,
}: UseCloseTemplateLifecycleParams) {
    const { toast: successToast, error: errorToast } = useToaster();

    const [deletingTemplate, setDeletingTemplate] =
        useState<CloseTemplateWithStats | null>(null);
    const [archivingTemplate, setArchivingTemplate] =
        useState<CloseTemplateWithStats | null>(null);
    const [unarchivingTemplate, setUnarchivingTemplate] =
        useState<CloseTemplateWithStats | null>(null);

    const deleteTemplateMutation = useDeleteCloseTemplateMutation();
    const archiveTemplateMutation = useArchiveCloseTemplateMutation();
    const unarchiveTemplateMutation = useUnarchiveCloseTemplateMutation();

    const makeHandler = useCallback(
        (setter: (template: CloseTemplateWithStats) => void) =>
            (closeTemplateId: string) => {
                const template = templatesList.find(
                    (t) => t.id === closeTemplateId,
                );
                if (template) {
                    setter(template);
                }
            },
        [templatesList],
    );

    const handleDeleteCloseTemplate = makeHandler(setDeletingTemplate);
    const handleArchiveCloseTemplate = makeHandler(setArchivingTemplate);
    const handleUnarchiveCloseTemplate = makeHandler(setUnarchivingTemplate);

    const lifecycleModals = (
        <>
            <ConfirmationModal
                show={!!deletingTemplate}
                title={`Delete "${deletingTemplate?.name}"?`}
                question="This template will be permanently removed."
                yes="Delete"
                no="Cancel"
                onReject={() => setDeletingTemplate(null)}
                onConfirm={async () => {
                    if (!deletingTemplate) {
                        return;
                    }
                    const templateName = deletingTemplate.name;
                    try {
                        await deleteTemplateMutation.mutateAsync(
                            deletingTemplate.id,
                        );
                        setDeletingTemplate(null);
                        successToast(`Deleted template "${templateName}"`);
                    } catch {
                        errorToast(
                            `Failed to delete template "${templateName}"`,
                        );
                    }
                }}
            />

            <ConfirmationModal
                show={!!archivingTemplate}
                title={`Archive "${archivingTemplate?.name}"?`}
                question="Archived templates are hidden by default. You can reactivate them at any time."
                yes="Archive"
                no="Cancel"
                onReject={() => setArchivingTemplate(null)}
                onConfirm={async () => {
                    if (!archivingTemplate) {
                        return;
                    }
                    try {
                        await archiveTemplateMutation.mutateAsync(
                            archivingTemplate.id,
                        );
                        setArchivingTemplate(null);
                        successToast(
                            `Archived template "${archivingTemplate.name}"`,
                        );
                    } catch {
                        errorToast(
                            `Failed to archive template "${archivingTemplate.name}"`,
                        );
                    }
                }}
            />

            <ConfirmationModal
                show={!!unarchivingTemplate}
                title={`Activate "${unarchivingTemplate?.name}"?`}
                question="This template will be restored and visible in your active templates list."
                yes="Activate"
                no="Cancel"
                onReject={() => setUnarchivingTemplate(null)}
                onConfirm={async () => {
                    if (!unarchivingTemplate) {
                        return;
                    }
                    try {
                        await unarchiveTemplateMutation.mutateAsync(
                            unarchivingTemplate.id,
                        );
                        setUnarchivingTemplate(null);
                        successToast(
                            `Enabled template "${unarchivingTemplate.name}"`,
                        );
                    } catch {
                        errorToast(
                            `Failed to enable template "${unarchivingTemplate.name}"`,
                        );
                    }
                }}
            />
        </>
    );

    return {
        handleDeleteCloseTemplate,
        handleArchiveCloseTemplate,
        handleUnarchiveCloseTemplate,
        lifecycleModals,
    };
}
